import { raiseError, raiseConfirmation } from 'data/messages/messages.actions';
import { removeToken, saveToken } from 'services/auth.service';
import getErrorMessageFromStatus from 'utils/getErrorMessageFromStatus';
import { globalKey } from 'data/sharedKeys';
import AccountsApi from 'data/accounts/accounts.api';
import { loadAccountSuccess } from 'data/accounts/accounts.actions';
import LoginApi from './login.api';
import loginErrorMessages from './login.errors';

export const LOGIN_START = Symbol('LOGIN_START');
export const LOGIN_SUCCESS = Symbol('LOGIN_SUCCESS');
export const LOGIN_ERROR = Symbol('LOGIN_ERROR');

export const FORGOT_PASSWORD_START = Symbol('FORGOT_PASSWORD_START');
export const FORGOT_PASSWORD_SUCCESS = Symbol('FORGOT_PASSWORD_SUCCESS');

export const RESET_PASSWORD_START = Symbol('RESET_PASSWORD_START');
export const RESET_PASSWORD_SUCCESS = Symbol('RESET_PASSWORD_SUCCESS');
export const RESET_PASSWORD_ERROR = Symbol('RESET_PASSWORD_ERROR');

const loginStart = () => ({ type: LOGIN_START });
const loginSuccess = () => ({ type: LOGIN_SUCCESS });
const loginError = () => ({ type: LOGIN_ERROR });

const raiseLoginError = error => raiseError(error.response ? new Error(getErrorMessageFromStatus(error.response.status, loginErrorMessages)) : error);

const getHandleLogin = dispatch => function handleLogin(response) {
  const token = response.headers['platform-token'];
  if (token != null && token !== 'undefined') {
    // first save the token we received, then immediately attempt to verify that the user is authorized for this application
    saveToken(token);
    return AccountsApi.isAuthorized()
      .then(() => (
        AccountsApi.getAccount()
          .then((account) => {
            dispatch(loadAccountSuccess(globalKey, account));
            sessionStorage.setItem('IsProducer',account.data.IsProducer);
          })
          .catch((error) => {
            if (error.response && error.response.status === 404) {
              dispatch(loadAccountSuccess(globalKey, { data: {} }));
              return;
            }

            throw error;
          })
      ))
      .then(() => dispatch(loginSuccess()))
      .catch((error) => {
        removeToken();
        throw error;
      });
  }
  throw new Error('Login Failed: did not receive token');
};

export function login(username, password) {
  return function loginThunk(dispatch) {
    dispatch(loginStart());
    // eslint-disable-next-line import/no-named-as-default-member
    return LoginApi.login(username, password)
      .then(getHandleLogin(dispatch))
      .catch((error) => {
        dispatch(raiseLoginError(error));
        dispatch(loginError());
      });
  };
}

export function ssoLogin(ssoToken) {
  return function loginThunk(dispatch) {
    dispatch(loginStart());
    // eslint-disable-next-line import/no-named-as-default-member
    return LoginApi.ssoLogin(ssoToken)
      .then(getHandleLogin(dispatch))
      .catch((error) => {
        dispatch(raiseLoginError(error));
        dispatch(loginError());
      });
  };
}


export function createUser(username, password) {
  return function createUserThunk(dispatch) {
    dispatch(loginStart());
    // eslint-disable-next-line import/no-named-as-default-member
    return LoginApi.createUser(username, password)
      .then(getHandleLogin(dispatch))
      .catch((error) => {
        dispatch(raiseLoginError(error));
        dispatch(loginError());
      });
  };
}

export function forgotPassword(email) {
  return function forgotPasswordThunk(dispatch) {
    dispatch({ type: FORGOT_PASSWORD_START });
    // eslint-disable-next-line import/no-named-as-default-member
    return LoginApi.forgotPassword(email)
      .then(() => {
        dispatch({ type: FORGOT_PASSWORD_SUCCESS });
        dispatch(raiseConfirmation('If an account exists with that email, a password reset link has been sent.'));
      })
      .catch((error) => {
        if (error.response && error.response.status === 400) {
          dispatch(raiseError(new Error(
            (error.response.data && error.response.data.message) || 'Please enter a valid email address'
          )));
          return;
        }
        dispatch({ type: FORGOT_PASSWORD_SUCCESS });
        dispatch(raiseConfirmation('If an account exists with that email, a password reset link has been sent.'));
      });
  };
}

export function resetPassword(token, newPassword, confirmPassword) {
  return function resetPasswordThunk(dispatch) {
    dispatch({ type: RESET_PASSWORD_START });
    // eslint-disable-next-line import/no-named-as-default-member
    return LoginApi.resetPassword(token, newPassword, confirmPassword)
      .then(() => {
        dispatch({ type: RESET_PASSWORD_SUCCESS });
      })
      .catch((error) => {
        dispatch({ type: RESET_PASSWORD_ERROR });
        dispatch(raiseError(error.response
          ? new Error(getErrorMessageFromStatus(error.response.status, loginErrorMessages))
          : error));
      });
  };
}
